import React, { FC, useState } from 'react';
import Modal from 'react-modal';
import { useParams } from 'react-router-dom';
import * as T from 'fp-ts/Task';
import * as TE from 'fp-ts/TaskEither';
import { pipe } from 'fp-ts/function';
import { AccountType, setAccountTypeCookie } from '@shared/utils/cookies';
import { fetchVisit, transformIdForAccountType } from '@modules/visits/context';
import { useCurrentLanguage } from '@shared/modules/translation/hooks';
import * as Styled from './AccountTypeModal.style';
import { useTranslation } from 'react-i18next';

interface AccountTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountTypeModal: FC<AccountTypeModalProps> = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState<AccountType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { id } = useParams<'id'>() as { id?: string };
  const language = useCurrentLanguage();
  const { t } = useTranslation();

  const handleConfirm = async () => {
    if (!selectedType) return;
    
    setIsLoading(true);

    try {
      if (id) {
        const newId = transformIdForAccountType(id, selectedType);
        
        await pipe(
          fetchVisit(newId, language),
          TE.fold(
            () => T.of(void 0),
            () => T.of(void 0),
          ),
        )();
      }

      setAccountTypeCookie(selectedType);
      onClose();
      
      window.location.reload();
    } catch (error) {
      setAccountTypeCookie(selectedType);
      onClose();
      window.location.reload();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t('accountTypeModal.title')}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        },
        content: {
          border: 'none',
          borderRadius: '12px',
          padding: 0,
          inset: 'auto',
          position: 'static',
          maxWidth: '500px',
          width: '90%',
          margin: '0 auto',
        },
      }}>
      <Styled.ModalContent>
        <Styled.ModalTitle>{t('accountTypeModal.title')}</Styled.ModalTitle>
        <Styled.ModalDescription>{t('accountTypeModal.description')}</Styled.ModalDescription>
        <Styled.ModalNote>{t('accountTypeModal.note')}</Styled.ModalNote>

        <Styled.OptionsContainer>
          <Styled.OptionButton isSelected={selectedType === 'standard'} onClick={() => setSelectedType('standard')}>
            <Styled.OptionTitle>{t('accountTypeModal.standard.title')}</Styled.OptionTitle>
            <Styled.OptionDescription>{t('accountTypeModal.standard.description')}</Styled.OptionDescription>
          </Styled.OptionButton>

          <Styled.OptionButton isSelected={selectedType === 'scholar'} onClick={() => setSelectedType('scholar')}>
            <Styled.OptionTitle>{t('accountTypeModal.scholar.title')}</Styled.OptionTitle>
            <Styled.OptionDescription>{t('accountTypeModal.scholar.description')}</Styled.OptionDescription>
          </Styled.OptionButton>
        </Styled.OptionsContainer>

        <Styled.ConfirmButton onClick={handleConfirm} disabled={!selectedType || isLoading}>
          {isLoading ? t('accountTypeModal.loading', 'Chargement...') : t('accountTypeModal.confirm')}
        </Styled.ConfirmButton>
      </Styled.ModalContent>
    </Modal>
  );
};

export default AccountTypeModal;
